import React, { useState } from "react";
import { MapContainer, TileLayer, useMap, CircleMarker, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";
// Import all state data
import { saInstallsData } from "./data/sa_installs";
import { saQuotesData } from "./data/sa_quotes";
import { nswInstallsData } from "./data/nsw_installs";
import { nswQuotesData } from "./data/nsw_quotes";
import { vicInstallsData } from "./data/vic_installs";
import { vicQuotesData } from "./data/vic_quotes";
import { qldInstallsData } from "./data/qld_installs";
import { qldQuotesData } from "./data/qld_quotes";
import { waInstallsData } from "./data/wa_installs";
import { waQuotesData } from "./data/wa_quotes";
import { actInstallsData } from "./data/act_installs";
import { actQuotesData } from "./data/act_quotes";
import { ntInstallsData } from "./data/nt_installs";
import { ntQuotesData } from "./data/nt_quotes";

// State configuration
const STATE_CONFIG = {
  SA: {
    name: "South Australia",
    center: [-34.9285, 138.6007],
    zoom: 10,
    installsData: saInstallsData,
    quotesData: saQuotesData
  },
  NSW: {
    name: "New South Wales",
    center: [-33.8688, 151.2093],
    zoom: 10,
    installsData: nswInstallsData,
    quotesData: nswQuotesData
  },
  VIC: {
    name: "Victoria",
    center: [-37.8136, 144.9631],
    zoom: 10,
    installsData: vicInstallsData,
    quotesData: vicQuotesData
  },
  QLD: {
    name: "Queensland",
    center: [-27.4698, 153.0251],
    zoom: 10,
    installsData: qldInstallsData,
    quotesData: qldQuotesData
  },
  WA: {
    name: "Western Australia",
    center: [-31.9505, 115.8605],
    zoom: 10,
    installsData: waInstallsData,
    quotesData: waQuotesData
  },
  ACT: {
    name: "Australian Capital Territory",
    center: [-35.2809, 149.1300],
    zoom: 11,
    installsData: actInstallsData,
    quotesData: actQuotesData
  },
  NT: {
    name: "Northern Territory",
    center: [-12.4634, 130.8456],
    zoom: 10,
    installsData: ntInstallsData,
    quotesData: ntQuotesData
  }
};

// CARTO basemaps require an API key, set at build time (Coolify Build Variable in production, .env.local locally)
// Get one at https://carto.com/basemaps/apikey
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY;

function HeatmapLayer({ points, gradient }) {
  const map = useMap();

  React.useEffect(() => {
    if (!points || points.length === 0) return;

    const heat = L.heatLayer(points, {
      radius: 30,
      blur: 30,
      gradient: gradient
    }).addTo(map);

    return () => {
      map.removeLayer(heat);
    };
  }, [map, points, gradient]);

  return null;
}

function MapController({ center, zoom }) {
  const map = useMap();
  
  React.useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  
  return null;
}

export default function App() {
  const [selectedState, setSelectedState] = useState("SA");
  const [showInstalls, setShowInstalls] = useState(true);
  const [showQuotes, setShowQuotes] = useState(false);

  const currentState = STATE_CONFIG[selectedState];
  const installsPoints = currentState.installsData.map((s) => [s.lat, s.lng, s.value * 2.5]);
  const quotesPoints = currentState.quotesData.map((s) => [s.lat, s.lng, s.value * 2.5]);

  const controlPanelStyle = {
    position: "absolute",
    top: "40px",
    left: "60px",
    zIndex: 1000,
    background: "white",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    minWidth: "300px",
    fontFamily: 'Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
  };

  const titleStyle = {
    margin: "0 0 15px 0",
    fontSize: "18px",
    fontWeight: "bold",
    color: "#333"
  };

  const selectStyle = {
    width: "100%",
    padding: "8px",
    marginBottom: "15px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px"
  };

  const installsGradient = {
    0.01: '#fff5f0',
    0.1: '#fcbba1',
    0.3: '#fc9272',
    0.5: '#fb6a4a',
    0.7: '#de2d26',
    1.0: '#a50f15'
  };

  const quotesGradient = {
    0.01: '#e3f0ff',
    0.1: '#a3cfff',
    0.3: '#72b2fc',
    0.5: '#4a90fb',
    0.7: '#265ede',
    1.0: '#153fa5'
  };

  return (
    <div style={{ height: "100vh", width: "100vw", position: "relative" }}>
      {/* Control Panel */}
      <div style={controlPanelStyle}>
        <h2 style={titleStyle}>Sales and Quotes Heatmap</h2>
        
        <div>
          <label htmlFor="state-select" style={{ display: "block", marginBottom: "5px", fontSize: "14px", fontWeight: "500" }}>
            Select State:
          </label>
          <select 
            id="state-select"
            value={selectedState} 
            onChange={(e) => setSelectedState(e.target.value)}
            style={selectStyle}
          >
            {Object.keys(STATE_CONFIG).map(stateCode => (
              <option key={stateCode} value={stateCode}>
                {STATE_CONFIG[stateCode].name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", fontWeight: "500" }}>
            Data Type:
          </label>
          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}>
              <input
                type="checkbox"
                checked={showInstalls}
                onChange={() => setShowInstalls((v) => !v)}
              />
              Sales
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}>
              <input
                type="checkbox"
                checked={showQuotes}
                onChange={() => setShowQuotes((v) => !v)}
              />
              Quotes
            </label>
          </div>
        </div>
      </div>

      <MapContainer
        center={currentState.center}
        zoom={currentState.zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
        wheelPxPerZoomLevel={180}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, © <a href="https://carto.com/attributions">CARTO</a>'
          url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
        />
        <MapController center={currentState.center} zoom={currentState.zoom} />
        {showInstalls && (
          <HeatmapLayer points={installsPoints} gradient={installsGradient} />
        )}
        {showQuotes && (
          <HeatmapLayer points={quotesPoints} gradient={quotesGradient} />
        )}
        {showInstalls && currentState.installsData.map((s, i) => (
          <CircleMarker
            key={i}
            center={[s.lat, s.lng]}
            radius={1}
            pathOptions={{ color: "#444", fillColor: "#444", fillOpacity: 0.5, weight: 0.5 }}
          />
        ))}
        {showQuotes && currentState.quotesData.map((s, i) => (
          <CircleMarker
            key={`q-${i}`}
            center={[s.lat, s.lng]}
            radius={1}
            pathOptions={{ color: "#2356a5", fillColor: "#4a90fb", fillOpacity: 0.5, weight: 0.5 }}
          />
        ))}
      </MapContainer>
    </div>
  );
}